import React, { useEffect, useState } from 'react';
import { Loader, Modal, Dimmer, Icon } from 'semantic-ui-react';
import { SelectableModalProps } from '../../../../../components/types';
import styled from 'styled-components';
import { HiSparkles } from 'react-icons/hi';
import FollowUpTab from '../Tabs/FollowUpTab';
import ActionHistoryTab from '../Tabs/ActionHistoryTab';
import { ClaimPendencyValues } from '../../../../../components/ClaimFollowUpPendencyFields';
import { getClaims } from '../../../../../services/claim';
import PortoLogsTab from '../Tabs/PortoLogsTab';
import GaranttiLogsTab from '../Tabs/GaranttiLogsTab';
import SynthesisTab from '../Tabs/SynthesisTab';
import { canUseFollowUpRewrite } from '../../../../../utils/canUseFollowUpRewrite';

const CLAIM_UPDATES_ACCENT_COLOR = '#59249B';
const GARANTTI_ORANGE = '#f2711c';
const PORTO_INSURER_CODES = ['PORTO', 'PORTO_ESSENCIAL'];

const StyledModal = styled(Modal)`
  &&& {
    width: min(1440px, 96vw) !important;
  }

  &&& > .header {
    color: #2f3441;
    border-bottom: 1px solid #e5e7eb;
  }

  &&& > .header i.icon {
    color: ${CLAIM_UPDATES_ACCENT_COLOR};
  }

  &&& > .scrolling.content {
    max-height: calc(92vh - 6rem);
  }
`;

const TabsSection = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  margin: 0 -1.5rem 22px;
  padding: 0 1.5rem 20px;
`;

const TabContainer = styled.div<{ $tabs: number }>`
  border: 1px solid #dddddd;
  border-radius: 4px;
  display: flex;
  flex-direction: row;
  width: ${props => (props.$tabs === 3 ? '400px' : '224px')};
  max-width: 100%;
  min-width: ${props => (props.$tabs === 3 ? 'min(400px, 100%)' : '224px')};
`;

const TabsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SynthesisButton = styled.button<{ $active: boolean }>`
  width: 38px;
  height: 38px;
  border: 0;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${props => (props.$active ? '#59249B' : '#8d4de6')};
  color: #ffffff;
  cursor: pointer;
  font-size: 20px;

  &:disabled {
    opacity: 0.85;
    cursor: wait;
  }

  &:hover {
    background: #59249B;
  }
`;

const TabMenu = styled.div<{ actived: boolean; $tabs: number }>`
  width: ${props => (props.$tabs === 3 ? '33.34%' : '50%')};
  min-width: 0;
  padding: 8px 10px;
  background-color: ${props => (props.actived ? CLAIM_UPDATES_ACCENT_COLOR : '#ffffff')};
  color: ${props => (props.actived ? '#ffffff' : CLAIM_UPDATES_ACCENT_COLOR)};
  cursor: ${props => (props.actived ? '' : 'pointer')};
  font-weight: ${props => (props.actived ? 700 : 400)};
  text-align: center;
`;

const TabLabel = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const UnreadDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${GARANTTI_ORANGE};
  flex-shrink: 0;
`;

export type FollowUpModalProps = {
  onClose: () => any;
  onOpen: () => any;
  id: string;
  claim: any;
  isBlocked?: boolean;
  user: any;
  initialTab?: TabProps;
} & SelectableModalProps;

export type TabProps =
  | 'follow-up'
  | 'action-history'
  | 'porto'
  | 'garantti'
  | 'synthesis';

const isPortoClaim = (claim: any): boolean => {
  const code = String(claim?.insurerCode || '').toUpperCase();
  return PORTO_INSURER_CODES.includes(code);
};

const isGaranttiClaim = (claim: any): boolean =>
  String(claim?.insurerCode || '').toUpperCase() === 'GARANTTI';

const headerIcon = (tab: TabProps) => {
  if (tab === 'synthesis') {
    return 'magic';
  }
  if (tab === 'porto') {
    return 'building';
  }
  if (tab === 'garantti') {
    return 'comments';
  }
  return tab === 'follow-up' ? 'shield alternate' : 'history';
};

const resolveInitialTab = (
  initialTab: TabProps | undefined,
  showPortoTab: boolean,
  showGaranttiTab: boolean,
  showSynthesisTab: boolean
): TabProps => {
  if (initialTab === 'garantti' && showGaranttiTab) {
    return 'garantti';
  }
  if (initialTab === 'porto' && showPortoTab) {
    return 'porto';
  }
  if (initialTab === 'action-history') {
    return 'action-history';
  }
  if (initialTab === 'synthesis' && showSynthesisTab) {
    return 'synthesis';
  }
  return 'follow-up';
};

const ClaimUpdatesListModal = (props: FollowUpModalProps) => {
  const { header, onClose, onOpen, isOpen, id, claim, user, isBlocked, initialTab } = props;
  const [isLoading] = useState<boolean>(false);
  const [isSynthesisLoading, setIsSynthesisLoading] = useState(false);
  const hasActionHistoryPermission = !user.role.includes('ESTATE');
  const showSynthesisButton =
    hasActionHistoryPermission && canUseFollowUpRewrite(user?.role);
  const showPortoTab = hasActionHistoryPermission && isPortoClaim(claim);
  const showGaranttiTab = hasActionHistoryPermission && isGaranttiClaim(claim);
  const tabCount = showPortoTab || showGaranttiTab ? 3 : 2;
  const [tabActive, setTabActive] = useState<TabProps>(
    resolveInitialTab(
      initialTab,
      showPortoTab,
      showGaranttiTab,
      showSynthesisButton
    )
  );
  const showGaranttiTabDot = Number(claim?.unreadGaranttiLogsCount || 0) > 0;
  const [claimData, setClaimData] = useState<any>(claim);

  useEffect(() => {
    let isMounted = true;

    setClaimData(claim);

    if (!isOpen || !id) {
      return () => {
        isMounted = false;
      };
    }

    // The claim passed by the table can be stale after a previous update.
    // Refresh the process-level pendency fields whenever the modal opens so
    // that a regular follow-up never starts from an old responsible/type.
    getClaims({ id })
      .then(currentClaim => {
        if (!isMounted || !currentClaim) return;

        setClaimData((previousClaim: any) => ({
          ...previousClaim,
          ...(Object.prototype.hasOwnProperty.call(currentClaim, 'pendingResponsible')
            ? { pendingResponsible: currentClaim.pendingResponsible }
            : {}),
          ...(Object.prototype.hasOwnProperty.call(currentClaim, 'pendingType')
            ? { pendingType: currentClaim.pendingType }
            : {})
        }));
      })
      .catch(error => {
        console.error('Erro ao atualizar o status de pendência do sinistro', error);
      });

    return () => {
      isMounted = false;
    };
  }, [claim, id, isOpen]);

  const handlePendencyUpdated = (pendency: ClaimPendencyValues) => {
    setClaimData((currentClaim: any) => ({
      ...currentClaim,
      pendingResponsible: pendency.responsible,
      pendingType: pendency.type
    }));
  };

  const handleDisplay = (hasPermission: boolean) => {
    const classFirstTab = {
      borderRight: '1px solid #dddddd',
      borderTopLeftRadius: '4px',
      borderBottomLeftRadius: '4px'
    };
    const classMiddleTab = {
      borderRight: '1px solid #dddddd'
    };
    const classLastTab = {
      borderTopRightRadius: '4px',
      borderBottomRightRadius: '4px'
    };
    const classTabActive = { backgroundColor: CLAIM_UPDATES_ACCENT_COLOR, color: '#FFFFFF' };
    const classTabInactive = { cursor: 'pointer' };

    const classTabFollowup =
      tabActive === 'follow-up'
        ? Object.assign({}, classFirstTab, classTabActive)
        : Object.assign({}, classFirstTab, classTabInactive);

    const historyBase = showPortoTab || showGaranttiTab ? classMiddleTab : classLastTab;
    const classTabHistory =
      tabActive === 'action-history'
        ? Object.assign({}, historyBase, classTabActive)
        : Object.assign({}, historyBase, classTabInactive);

    const classTabInsurer =
      tabActive === 'porto' || tabActive === 'garantti'
        ? Object.assign({}, classLastTab, classTabActive)
        : Object.assign({}, classLastTab, classTabInactive);

    return hasPermission ? (
      <>
        <TabsSection>
          <TabsRow>
            {showSynthesisButton && (
              <SynthesisButton
                type="button"
                $active={tabActive === 'synthesis'}
                onClick={() => {
                  setIsSynthesisLoading(true);
                  setTabActive('synthesis');
                }}
                disabled={isSynthesisLoading}
                aria-busy={isSynthesisLoading}
                title={
                  isSynthesisLoading
                    ? 'Gerando síntese do sinistro...'
                    : 'Gerar síntese do sinistro com IA'
                }
                aria-label={
                  isSynthesisLoading
                    ? 'Síntese do sinistro sendo gerada'
                    : 'Gerar síntese do sinistro com IA'
                }
              >
                {isSynthesisLoading ? (
                  <Loader active inline="centered" size="tiny" />
                ) : (
                  <HiSparkles />
                )}
              </SynthesisButton>
            )}
            <TabContainer $tabs={tabCount}>
              <TabMenu
                actived={tabActive === 'follow-up'}
                $tabs={tabCount}
                style={classTabFollowup}
                onClick={() => setTabActive('follow-up')}
              >
                Geral
              </TabMenu>
              <TabMenu
                actived={tabActive === 'action-history'}
                $tabs={tabCount}
                style={classTabHistory}
                onClick={() => setTabActive('action-history')}
              >
                Histórico
              </TabMenu>
              {showPortoTab && (
                <TabMenu
                  actived={tabActive === 'porto'}
                  $tabs={tabCount}
                  style={classTabInsurer}
                  onClick={() => setTabActive('porto')}
                >
                  Porto
                </TabMenu>
              )}
              {showGaranttiTab && (
                <TabMenu
                  actived={tabActive === 'garantti'}
                  $tabs={tabCount}
                  style={classTabInsurer}
                  onClick={() => setTabActive('garantti')}
                >
                  <TabLabel>
                    Garantti
                    {showGaranttiTabDot ? (
                      <UnreadDot aria-hidden="true" title="Atualizações não lidas da Garantti" />
                    ) : null}
                  </TabLabel>
                </TabMenu>
              )}
            </TabContainer>
          </TabsRow>
        </TabsSection>

        {tabActive === 'synthesis' && showSynthesisButton ? (
          <SynthesisTab
            id={id}
            claim={claim}
            onLoadingChange={setIsSynthesisLoading}
          />
        ) : tabActive === 'follow-up' ? (
          <FollowUpTab
            id={id}
            claim={claimData}
            isBlocked={isBlocked}
            onPendencyUpdated={handlePendencyUpdated}
          />
        ) : tabActive === 'action-history' ? (
          <ActionHistoryTab id={id} />
        ) : tabActive === 'porto' && showPortoTab ? (
          <PortoLogsTab id={id} claim={claim} />
        ) : tabActive === 'garantti' && showGaranttiTab ? (
          <GaranttiLogsTab id={id} claim={claim} />
        ) : null}
      </>
    ) : (
      <FollowUpTab id={id} claim={claimData} isBlocked={isBlocked} />
    );
  };

  return (
    <>
      <StyledModal size="large" open={isOpen} onOpen={onOpen} onClose={onClose} closeIcon>
        <Modal.Header>
          {hasActionHistoryPermission ? (
            <>
              <Icon name={headerIcon(tabActive)} />{' '}
            </>
          ) : null}
          {header}
        </Modal.Header>
        <Modal.Content scrolling>
          {isLoading ? (
            <Dimmer active={isLoading} inverted>
              <Loader content="Carregando mensagens..." />
            </Dimmer>
          ) : (
            handleDisplay(hasActionHistoryPermission)
          )}
        </Modal.Content>
        <Modal.Actions></Modal.Actions>
      </StyledModal>
    </>
  );
};

export default ClaimUpdatesListModal;
